import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FilePen, Plus } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { EmptyState } from '../../../components/EmptyState';
import { useAttendanceStore, type Adjustment } from '../../../store/attendanceStore';
import { AdjustmentDialog } from './AdjustmentDialog';
import { AdjustmentStatusBadge, adjustmentLabel, fmtDate, fmtDateTime, fmtMinutes, fmtTime } from './attendanceUi';

/** What was recorded vs what was asked for — shown for every request. */
export function AdjustmentComparison({ a }: { a: Adjustment }) {
    const o = a.original?.session;
    const recorded = o
        ? `${fmtTime(o.checkIn)} → ${o.checkOut ? fmtTime(o.checkOut) : 'no check-out'}${o.location ? ` · ${o.location}` : ''} (${fmtMinutes(o.creditedMinutes)})`
        : a.original?.day ? `Nothing recorded (${fmtMinutes(a.original.day.workedMinutes)} counted)` : 'Nothing recorded';
    const asked = [
        a.requestedCheckIn ? `in ${fmtTime(a.requestedCheckIn)}` : null,
        a.requestedCheckOut ? `out ${fmtTime(a.requestedCheckOut)}` : null,
        a.requestedLocationName ? `at ${a.requestedLocationName}` : null,
    ].filter(Boolean).join(', ') || 'See reason';
    return (
        <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <div><dt className="text-muted-foreground">Originally recorded</dt><dd className="text-foreground">{recorded}</dd></div>
            <div><dt className="text-muted-foreground">Requested</dt><dd className="font-medium text-foreground">{asked}</dd></div>
        </dl>
    );
}

/** The employee's own adjustment requests. */
export function AdjustmentsPanel() {
    const { adjustments, adjustmentsLoading, fetchAdjustments, cancelAdjustment } = useAttendanceStore();
    const [creating, setCreating] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    const load = () => fetchAdjustments({ mine: true }).catch((e) => toast.error(e.message));
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const cancel = async (id: string) => {
        setBusy(id);
        try { await cancelAdjustment(id); toast.success('Request cancelled'); } catch (e: any) { toast.error(e.message); } finally { setBusy(null); }
    };

    const mine = adjustments.filter((a) => a.isMine);
    return (
        <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div>
                    <h2 className="font-display text-card-title text-foreground">My adjustment requests</h2>
                    <p className="text-xs text-muted-foreground">Forgot to check in or out, or a time or place is wrong? Ask for a correction here.</p>
                </div>
                <Button onClick={() => setCreating(true)}><Plus /> New request</Button>
            </div>
            {mine.length === 0 ? (
                <EmptyState className="m-4 border-0" icon={<FilePen />} title={adjustmentsLoading ? 'Loading…' : 'No requests yet'}
                    description="Requests you send appear here with their review status." />
            ) : (
                <ul className="divide-y divide-border">
                    {mine.map((a) => (
                        <li key={a.id} className="space-y-2 px-4 py-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <p className="text-sm font-medium text-foreground">{adjustmentLabel(a.type)} · {fmtDate(a.date)}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {a.source === 'ADMIN' ? 'Recorded by HR' : `Sent ${fmtDateTime(a.createdAt)}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <AdjustmentStatusBadge status={a.status} />
                                    {a.status === 'pending' && (
                                        <Button size="sm" variant="ghost" onClick={() => cancel(a.id)} isLoading={busy === a.id}>Cancel</Button>
                                    )}
                                </div>
                            </div>
                            <AdjustmentComparison a={a} />
                            <p className="text-xs text-muted-foreground"><span className="text-foreground">Reason:</span> {a.reason}</p>
                            {a.reviewerName && a.status !== 'pending' && (
                                <p className="text-xs text-muted-foreground">
                                    {a.status === 'approved' ? 'Approved' : a.status === 'rejected' ? 'Rejected' : 'Reviewed'} by {a.reviewerName}
                                    {a.reviewedAt ? ` on ${fmtDateTime(a.reviewedAt)}` : ''}{a.reviewerComment ? ` — “${a.reviewerComment}”` : ''}
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            <AdjustmentDialog open={creating} onOpenChange={setCreating} onDone={load} />
        </Card>
    );
}
